import * as React from "react";
import { useReducer, useEffect } from "react";
import { FacetResult } from "../Models/FacetResult";
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';
import TooltipTrigger from 'react-popper-tooltip';
import * as moment from 'moment';
import cn from 'classnames';
import LoadingIndicator from "./LoadingIndicator";
import { ChapterArticle, CalendarChapterArticle } from "../Models/ChapterArticle";
import { CategoryModel } from "../Models/CategoryModel";

const localizer = momentLocalizer(moment);

export type SearchFilter = {
    Category: string;
    Locations: string[];
    SortASC: boolean;
    Path: string;
    StartDateString: string;
    LimitToNodeAliasPath?: boolean;
}

type EventsCalendarResultState = {

    items: CalendarChapterArticle[];
    totalItems: number;
    isNoResults: boolean;
    isLoading: boolean;
    canLoadMore: boolean;
    searchFilter: SearchFilter;
}


const initialState: EventsCalendarResultState = {
    items: [],
    totalItems: 0,
    isNoResults: false,
    isLoading: false,
    canLoadMore: false,
    searchFilter: undefined
}



const ChapterEventsCalendarWidget = (props: {
    Path: string;
    Categories: CategoryModel[];
    //searchFilter: SearchFilter;
    //searchByCategory: (categoryId: string) => void;
}) => {

    let [state, setState] = useReducer(
        (prevState: EventsCalendarResultState, newState: Partial<EventsCalendarResultState>) => ({ ...prevState, ...newState }),
        initialState
    );

    const search = () => {

        setState({ isLoading: true, canLoadMore: false });

        serviceCall(state.searchFilter, 0)
            .then(x =>
                setState({
                    items: x.items.map(x => CalendarChapterArticle.fromChapterArticle(x)),
                    isLoading: false
                })
            );

    }

    const serviceCall = (sf: SearchFilter, offset: number): Promise<FacetResult<ChapterArticle>> => {

        let payload = { ...sf, offset, pageSize: 10 };

        return fetch('/cpaapi/chaptercalendar', {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        }).then(res => res.json());

    };

    const defaultDate = React.useMemo(() => {
        let date = new Date();
        date.setDate(1);
        return date;
    }, []);

    const onView = (view: string) => {

        if (view != "month") return;

        const changeDate = new Date(state.searchFilter.StartDateString);

        if (changeDate.getDate() == 1) return;

        changeDate.setDate(1);

        const newSearchFilter = { ...state.searchFilter };
        newSearchFilter.StartDateString = moment(changeDate).utcOffset(0, true).format("YYYY-MM-DD");

        setState({ searchFilter: newSearchFilter });
    }

    const onNavigate = (startDate, view: string, eventName: string) => {
        // fix startDate issue when switching view from date to month

        if (view == "month" && eventName != "DATE") {
            startDate.setDate(1);
        }

        const newSearchFilter = { ...state.searchFilter };
        newSearchFilter.StartDateString = moment(startDate).utcOffset(0, true).format("YYYY-MM-DD");


        setState({ searchFilter: newSearchFilter });
    }


    // update on new search filter
    useEffect(() => {
        if (state.searchFilter)
            search();

    }, [state.searchFilter]);


    // update on new search filter
    useEffect(() => {

        const today = new Date();
        today.setDate(1);

        setState({
            searchFilter: {
                Category: null,
                Locations: [],
                Path: props.Path,
                SortASC: true,
                LimitToNodeAliasPath: true,
                StartDateString: moment(today).utcOffset(0, true).format("YYYY-MM-DD")
            }
        })

    }, []);

    return <>
        <div className="calendarWrapper">
            <Calendar
                events={state.items}
                views={['month']}
                step={60}
                popup={true}
                showMultiDayTimes
                //max={dates.add(dates.endOf(new Date(2015, 17, 1), 'day'), -1, 'hours')}                              
                defaultDate={defaultDate}
                //tooltipAccessor={(this.tooltipAccessor)}
                components={{
                    event: CalendarDisplayEvent,
                    toolbar: CustomToolbar
                }}
                loading={state.isLoading}
                localizer={localizer}
                eventPropGetter={(event: ChapterArticle, start, end, isSelected) => {
                    return {
                        style: { backgroundColor: event.Category ? event.Category.BackgroundColor : 'unset' }
                    };
                }}
                allDayAccessor={(event) => true}
                //slotPropGetter={(this.slotPropGetter)}
                dayPropGetter={(date) => {
                    return {
                        className: (date.getDay() == 0 || date.getDay() == 6) ? ' day-weekend ' : ''
                    };
                }}
                onSelectEvent={(event: ChapterArticle, e) => {
                    //console.log('onSelectEvent', event);
                    if (event && event.Url) { e.preventDefault(); window.location.href = event.Url; }
                }}
                onNavigate={onNavigate}
                onView={onView}
            />

            <ul className="calendar-legend">
                {props.Categories.map((category, index) => {
                    return (
                        <li key={(category.Name + index)}>
                            <span style={{ backgroundColor: category.BackgroundColor }}></span>
                            {category.Name}
                        </li>)

                })}
            </ul>
        </div>
    </>
}

class CalendarDisplayEvent extends React.Component<{ event: CalendarChapterArticle }, {}> {

    buildTooltip = ({
        arrowRef,
        tooltipRef,
        getArrowProps,
        getTooltipProps,
        placement
    }) => {

        return (
            <div
                {...getTooltipProps({
                    ref: tooltipRef,
                    className: 'calendar-tooltip-container'
                    /* your props here */
                })}
            >
                <div
                    {...getArrowProps({
                        ref: arrowRef,
                        className: 'tooltip-arrow',
                        'data-placement': placement
                        /* your props here */
                    })}
                />
                <div className="calendar-tooltip event-item">
                    <h3 className="title">
                        {this.props.event.Title}
                    </h3>                   
                    <div className="chapter">
                        {this.props.event.Chapter}
                    </div>
                    <div className="abstract" dangerouslySetInnerHTML={{ __html: this.props.event.Summary }}>

                    </div>
                </div>
            </div>
        )
    };

    buildEvent = ({ getTriggerProps, triggerRef }) => {

        const style = { backgroundColor: this.props.event.Category.BackgroundColor };

        return (
            <div
                {...getTriggerProps({
                    ref: triggerRef,
                    className: 'trigger',
                })} style={style}
            >
                {this.props.event.Title}
            </div>);
    }

    render() {
        return (<TooltipTrigger placement="auto" trigger="hover" tooltip={this.buildTooltip}>
            {this.buildEvent}
        </TooltipTrigger>)
    }
}

const navigate = {
    PREVIOUS: 'PREV',
    NEXT: 'NEXT',
    TODAY: 'TODAY',
    DATE: 'DATE'
};

class CustomToolbar extends React.Component<{
    view: string;
    views: string[];
    label: React.ReactNode;
    localizer: any;
    onNavigate: (action: any) => void;
    onView: (view: string) => void;
    loading: boolean;
}> {
    render() {
        let {
            localizer: { messages },
            label,
        } = this.props

        //const btnToday = (<button type="button" onClick={this.navigate.bind(null, navigate.TODAY)}>{messages.today}</button>);
        //const btnPrevious = (<button type="button" onClick={this.navigate.bind(null, navigate.PREVIOUS)}>{messages.previous}</button>);
        const btnPrevious = (<button type="button" onClick={this.navigate.bind(null, navigate.PREVIOUS)}><i className="fa fa-angle-left"></i></button>);
        //const btnNext = (<button type="button" onClick={this.navigate.bind(null, navigate.NEXT)}>{messages.next}</button>);
        const btnNext = (<button type="button" onClick={this.navigate.bind(null, navigate.NEXT)}><i className="fa fa-angle-right"></i></button>);
        const viewNamesButtons = (this.viewNamesGroup(messages));

        return (
            <div className="rbc-toolbar">
                <span className="rbc-btn-group">
                    {btnPrevious}
                    <span className="rbc-toolbar-label">{label}</span>
                    {btnNext}
                </span>
                <span className="rbc-toolbar-label"> {this.props.loading && <LoadingIndicator></LoadingIndicator>}</span>


                <span className="rbc-btn-group">
                    {viewNamesButtons}
                </span>
            </div>
        )
    }

    navigate = action => {
        this.props.onNavigate(action)
    }

    view = view => {
        this.props.onView(view)
    }

    viewNamesGroup(messages) {
        const viewNames = this.props.views
        const view = this.props.view

        if (viewNames.length > 1) {
            return viewNames.map(name => (
                <button
                    type="button"
                    key={name}
                    className={cn({ 'rbc-active': view === name })}
                    onClick={this.view.bind(null, name)}
                >
                    {messages[name]}
                </button>
            ))
        }
    }
}

export default ChapterEventsCalendarWidget;
